/**
 * AdminWithdrawals
 * 관리자 출금 관리 페이지
 */
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getWithdrawalRequests, approveWithdrawal, bulkApproveWithdrawals, type WithdrawalRequest } from '../../services/referral';
import { tokens } from '../../design-system/tokens';

export const AdminWithdrawals: React.FC = () => {
  const navigate = useNavigate();
  const { userInfo } = useAuth();
  const [withdrawals, setWithdrawals] = useState<WithdrawalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);

  // 관리자 권한 확인
  useEffect(() => {
    if (userInfo && userInfo.memberGrade !== 'admin') {
      navigate('/');
    }
  }, [userInfo, navigate]);

  // 출금 요청 목록 로드
  useEffect(() => {
    const loadWithdrawals = async () => {
      try {
        setLoading(true);
        const data = await getWithdrawalRequests(statusFilter || undefined);
        setWithdrawals(data);
      } catch (err: any) {
        console.error('Failed to load withdrawal requests:', err);
        setError('출금 요청을 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };

    loadWithdrawals();
  }, [statusFilter]);

  // 출금 승인 처리
  const handleApprove = async (withdrawalId: string) => {
    if (!window.confirm('출금을 승인하시겠습니까?')) {
      return;
    }

    try {
      setProcessingId(withdrawalId);
      await approveWithdrawal(withdrawalId, '관리자 승인');

      // 목록 새로고침
      const data = await getWithdrawalRequests(statusFilter || undefined);
      setWithdrawals(data);

      alert('출금이 승인되었습니다.');
    } catch (err: any) {
      console.error('Failed to approve withdrawal:', err);
      alert(err.message || '출금 승인에 실패했습니다.');
    } finally {
      setProcessingId(null);
    }
  };

  // CSV 내보내기
  const handleExportCSV = () => {
    if (withdrawals.length === 0) {
      alert('내보낼 데이터가 없습니다.');
      return;
    }

    // CSV 헤더
    const headers = [
      '요청일시',
      '사용자 이름',
      '사용자 이메일',
      '금액',
      '은행명',
      '계좌번호',
      '예금주명',
      '상태'
    ];

    // CSV 데이터
    const rows = withdrawals.map(withdrawal => [
      new Date(withdrawal.requestedAt).toLocaleString('ko-KR'),
      withdrawal.userName,
      withdrawal.userEmail,
      withdrawal.amount.toLocaleString(),
      withdrawal.bankAccount.bankName,
      `\t${withdrawal.bankAccount.accountNumber}`,  // 탭 문자로 텍스트 강제 (Excel 호환)
      withdrawal.bankAccount.accountHolder,
      withdrawal.status === 'pending' ? '대기' :
      withdrawal.status === 'approved' ? '승인됨' :
      withdrawal.status === 'completed' ? '완료' : '거부'
    ]);

    // CSV 생성
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // BOM 추가 (한글 Excel 호환)
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `withdrawals_${statusFilter || 'all'}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 일괄 출금 승인
  const handleBulkApprove = async () => {
    const pendingCount = withdrawals.filter(w => w.status === 'pending').length;

    if (pendingCount === 0) {
      alert('대기 중인 출금 요청이 없습니다.');
      return;
    }

    if (!window.confirm(`${pendingCount}건의 출금 요청을 완료 처리하시겠습니까?`)) {
      return;
    }

    try {
      setIsBulkProcessing(true);
      const result = await bulkApproveWithdrawals();

      // 목록 새로고침
      const data = await getWithdrawalRequests(statusFilter || undefined);
      setWithdrawals(data);

      // 결과 알림
      alert(`일괄 출금 완료\n성공: ${result.succeeded}건\n실패: ${result.failed}건\n총 ${result.processed}건 처리`);
    } catch (err: any) {
      console.error('Failed to bulk approve withdrawals:', err);
      alert(err.message || '일괄 출금 승인에 실패했습니다.');
    } finally {
      setIsBulkProcessing(false);
    }
  };

  return (
    <Content>
          <Header>
            <BackButton onClick={() => navigate('/admin')}>← 뒤로</BackButton>
            <Title>출금 관리</Title>
            <Spacer />
          </Header>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          {/* 상태 필터 */}
          <FilterSection>
            <FilterButton
              $active={statusFilter === 'pending'}
              onClick={() => setStatusFilter('pending')}
            >
              대기 중
            </FilterButton>
            <FilterButton
              $active={statusFilter === 'completed'}
              onClick={() => setStatusFilter('completed')}
            >
              완료
            </FilterButton>
            <FilterButton
              $active={statusFilter === ''}
              onClick={() => setStatusFilter('')}
            >
              전체
            </FilterButton>
          </FilterSection>

          {/* 액션 바 */}
          <ActionBar>
            <ActionBarLeft>
              {withdrawals.length}건의 출금 요청
            </ActionBarLeft>
            <ActionBarRight>
              <ActionButton
                onClick={handleExportCSV}
                disabled={withdrawals.length === 0 || loading}
              >
                📥 CSV 내보내기
              </ActionButton>
              {statusFilter === 'pending' && (
                <ActionButton
                  onClick={handleBulkApprove}
                  disabled={withdrawals.length === 0 || loading || isBulkProcessing}
                  $primary={true}
                >
                  {isBulkProcessing ? '처리 중...' : '✅ 일괄완료'}
                </ActionButton>
              )}
            </ActionBarRight>
          </ActionBar>

          {loading ? (
            <LoadingMessage>출금 요청을 불러오는 중...</LoadingMessage>
          ) : withdrawals.length === 0 ? (
            <EmptyMessage>출금 요청이 없습니다.</EmptyMessage>
          ) : (
            <WithdrawalsTable>
              <thead>
                <tr>
                  <th>요청일</th>
                  <th>사용자 정보</th>
                  <th>금액</th>
                  <th>계좌 정보</th>
                  <th>상태</th>
                  <th>액션</th>
                </tr>
              </thead>
              <tbody>
                {withdrawals.map((withdrawal) => (
                  <tr key={withdrawal.withdrawalId}>
                    <td>
                      {new Date(withdrawal.requestedAt).toLocaleString('ko-KR', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                    <td>
                      <UserInfo>
                        <UserName>{withdrawal.userName}</UserName>
                        <UserEmail>{withdrawal.userEmail}</UserEmail>
                      </UserInfo>
                    </td>
                    <td>
                      <AmountText>{withdrawal.amount.toLocaleString()}원</AmountText>
                    </td>
                    <td>
                      <BankInfo>
                        <div>{withdrawal.bankAccount.bankName}</div>
                        <div>{withdrawal.bankAccount.accountNumber}</div>
                        <div>{withdrawal.bankAccount.accountHolder}</div>
                      </BankInfo>
                    </td>
                    <td>
                      <StatusBadge $status={withdrawal.status}>
                        {withdrawal.status === 'pending' && '대기'}
                        {withdrawal.status === 'approved' && '승인됨'}
                        {withdrawal.status === 'completed' && '완료'}
                        {withdrawal.status === 'rejected' && '거부'}
                      </StatusBadge>
                    </td>
                    <td>
                      {withdrawal.status === 'pending' && (
                        <ApproveButton
                          onClick={() => handleApprove(withdrawal.withdrawalId)}
                          disabled={processingId === withdrawal.withdrawalId}
                        >
                          {processingId === withdrawal.withdrawalId ? '처리 중...' : '승인'}
                        </ApproveButton>
                      )}
                      {withdrawal.status === 'completed' && withdrawal.processedAt && (
                        <ProcessedInfo>
                          {new Date(withdrawal.processedAt).toLocaleDateString('ko-KR')}
                        </ProcessedInfo>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </WithdrawalsTable>
          )}
    </Content>
  );
};

// Styled Components
const Content = styled.div`
  padding: ${tokens.spacing[6]};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${tokens.spacing[6]};
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${tokens.colors.primary[500]};
  font-size: ${tokens.typography.fontSize.base};
  cursor: pointer;
  padding: ${tokens.spacing[2]};

  &:hover {
    color: ${tokens.colors.primary[600]};
  }
`;

const Title = styled.h1`
  font-size: ${tokens.typography.fontSize['2xl']};
  font-weight: ${tokens.typography.fontWeight.bold};
  color: ${tokens.colors.neutral[800]};
  margin: 0;
`;

const Spacer = styled.div`
  width: 60px;
`;

const ErrorMessage = styled.div`
  background: ${tokens.colors.semantic.error}15;
  color: ${tokens.colors.semantic.error};
  padding: ${tokens.spacing[4]};
  border-radius: ${tokens.borderRadius.md};
  margin-bottom: ${tokens.spacing[6]};
  text-align: center;
`;

const FilterSection = styled.div`
  display: flex;
  gap: ${tokens.spacing[2]};
  margin-bottom: ${tokens.spacing[6]};
`;

const FilterButton = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: ${tokens.spacing[3]};
  border: 1px solid ${props => props.$active ? tokens.colors.primary[500] : tokens.colors.neutral[300]};
  background: ${props => props.$active ? tokens.colors.primary[500] : tokens.colors.neutral[0]};
  color: ${props => props.$active ? tokens.colors.neutral[0] : tokens.colors.neutral[700]};
  border-radius: ${tokens.borderRadius.md};
  font-size: ${tokens.typography.fontSize.sm};
  font-weight: ${tokens.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${tokens.animation.duration.base} ${tokens.animation.easing.ease};

  &:hover {
    border-color: ${tokens.colors.primary[500]};
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: ${tokens.spacing[12]};
  color: ${tokens.colors.neutral[500]};
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: ${tokens.spacing[12]};
  color: ${tokens.colors.neutral[500]};
`;

const WithdrawalsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${tokens.typography.fontSize.sm};

  th {
    background: ${tokens.colors.neutral[100]};
    padding: ${tokens.spacing[3]};
    text-align: left;
    font-weight: ${tokens.typography.fontWeight.semibold};
    color: ${tokens.colors.neutral[700]};
    border-bottom: 2px solid ${tokens.colors.neutral[300]};
  }

  td {
    padding: ${tokens.spacing[3]};
    border-bottom: 1px solid ${tokens.colors.neutral[200]};
    vertical-align: top;
  }

  tr:hover td {
    background: ${tokens.colors.neutral[50]};
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacing[1]};
`;

const UserName = styled.div`
  font-weight: ${tokens.typography.fontWeight.medium};
  color: ${tokens.colors.neutral[800]};
  font-size: ${tokens.typography.fontSize.sm};
`;

const UserEmail = styled.div`
  font-size: ${tokens.typography.fontSize.xs};
  color: ${tokens.colors.neutral[600]};
`;

const AmountText = styled.div`
  font-weight: ${tokens.typography.fontWeight.semibold};
  color: ${tokens.colors.neutral[800]};
`;

const BankInfo = styled.div`
  font-size: ${tokens.typography.fontSize.xs};
  line-height: 1.4;
  color: ${tokens.colors.neutral[700]};

  div:first-child {
    font-weight: ${tokens.typography.fontWeight.medium};
  }
`;

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-block;
  padding: ${tokens.spacing[1]} ${tokens.spacing[3]};
  border-radius: ${tokens.borderRadius.full};
  font-size: ${tokens.typography.fontSize.xs};
  font-weight: ${tokens.typography.fontWeight.medium};
  background: ${props => {
    switch (props.$status) {
      case 'pending': return tokens.colors.semantic.warning + '15';
      case 'completed': return tokens.colors.semantic.success + '15';
      case 'rejected': return tokens.colors.semantic.error + '15';
      default: return tokens.colors.neutral[100];
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'pending': return tokens.colors.semantic.warning;
      case 'completed': return tokens.colors.semantic.success;
      case 'rejected': return tokens.colors.semantic.error;
      default: return tokens.colors.neutral[700];
    }
  }};
`;

const ApproveButton = styled.button`
  background: ${tokens.colors.semantic.success};
  color: ${tokens.colors.neutral[0]};
  border: none;
  padding: ${tokens.spacing[2]} ${tokens.spacing[4]};
  border-radius: ${tokens.borderRadius.sm};
  font-size: ${tokens.typography.fontSize.xs};
  font-weight: ${tokens.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${tokens.animation.duration.base} ${tokens.animation.easing.ease};

  &:hover:not(:disabled) {
    background: ${tokens.colors.semantic.success};
    filter: brightness(1.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ProcessedInfo = styled.div`
  font-size: ${tokens.typography.fontSize.xs};
  color: ${tokens.colors.neutral[600]};
`;

const ActionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${tokens.spacing[4]};
  padding: ${tokens.spacing[3]} ${tokens.spacing[4]};
  background: ${tokens.colors.neutral[50]};
  border-radius: ${tokens.borderRadius.md};
  border: 1px solid ${tokens.colors.neutral[200]};
`;

const ActionBarLeft = styled.div`
  font-size: ${tokens.typography.fontSize.sm};
  font-weight: ${tokens.typography.fontWeight.medium};
  color: ${tokens.colors.neutral[700]};
`;

const ActionBarRight = styled.div`
  display: flex;
  gap: ${tokens.spacing[2]};
`;

const ActionButton = styled.button<{ $primary?: boolean }>`
  padding: ${tokens.spacing[2]} ${tokens.spacing[4]};
  border: 1px solid ${props => props.$primary ? tokens.colors.primary[500] : tokens.colors.neutral[300]};
  background: ${props => props.$primary ? tokens.colors.primary[500] : tokens.colors.neutral[0]};
  color: ${props => props.$primary ? tokens.colors.neutral[0] : tokens.colors.neutral[700]};
  border-radius: ${tokens.borderRadius.sm};
  font-size: ${tokens.typography.fontSize.sm};
  font-weight: ${tokens.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${tokens.animation.duration.base} ${tokens.animation.easing.ease};

  &:hover:not(:disabled) {
    ${props => props.$primary ? `
      background: ${tokens.colors.primary[600]};
      border-color: ${tokens.colors.primary[600]};
    ` : `
      border-color: ${tokens.colors.primary[500]};
      color: ${tokens.colors.primary[500]};
    `}
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
